import {
    Checkbox as MuiCheck,
    CheckboxProps,
    makeStyles,
    Radio as MuiRadio,
    RadioProps,
    Switch as MuiSwitch,
    SwitchProps,
} from '@material-ui/core';
import clsx from 'clsx';
import React from 'react';
import { theme } from '../../../theme/theme';

const useInputClasses = makeStyles({
    root: {
        ':where(&)': {
            display: 'flex',
            alignItems: 'center',
            gap: 5,
            transition: `all ${theme.transitions.default}s`,
            '& > label': {
                color: theme.colors.neutral[1],
                ...theme.fonts.button,
            },
        },
    },
    input: {
        color: 'inherit',
    },
    switch: {
        width: 32,
        height: 16,
        padding: 0,
        '& .MuiSwitch-switchBase': {
            padding: 2,
            '&.Mui-checked': { transform: 'translateX(16px)' },
        },
        '& .MuiSwitch-track': {
            borderRadius: 8,
            backgroundColor: 'currentColor',
        },
        '& :not(.Mui-checked) + .MuiSwitch-track': { opacity: '0.5 !important' },
        '& .Mui-checked + .MuiSwitch-track': { opacity: '1 !important' },
        '& .MuiSwitch-thumb': { width: 12, height: 12 },
    },
});

/**
 * Labeled checkbox based on material-ui.
 *
 * @param props.label label
 * @param props.rootProp props for the root container
 */
export const Checkbox = (
    props: { label?: React.ReactNode; rootProps?: JSX.IntrinsicElements['div']; color?: never } & CheckboxProps,
) => {
    const { label, rootProps, ...inputProps } = props;
    const classes = useInputClasses();
    const id = React.useState(Math.random().toString())[0];

    return (
        <span {...rootProps} className={clsx(classes.root, rootProps?.className)}>
            <MuiCheck {...inputProps} id={id} className={clsx(classes.input, inputProps.className)} color='default' />
            {label != undefined && <label htmlFor={id}>{label}</label>}
        </span>
    );
};

/**
 * Labeled radio based on material-ui.
 *
 * @param props.label label
 * @param props.rootProp props for the root container
 */
export const Radio = (
    props: { label?: React.ReactNode; rootProps?: JSX.IntrinsicElements['div']; color?: never } & RadioProps,
) => {
    const { label, rootProps, ...inputProps } = props;
    const classes = useInputClasses();
    const id = React.useState(Math.random().toString())[0];

    return (
        <span {...rootProps} className={clsx(classes.root, rootProps?.className)}>
            <MuiRadio {...inputProps} id={id} className={clsx(classes.input, inputProps.className)} color='default' />
            {label != undefined && <label htmlFor={id}>{label}</label>}
        </span>
    );
};

/**
 * Labeled switch based on material-ui.
 *
 * @param props.label label
 * @param props.rootProp props for the root container
 */
export const Switch = (
    props: { label?: React.ReactNode; rootProps?: JSX.IntrinsicElements['div']; color?: never } & SwitchProps,
) => {
    const { label, rootProps, ...inputProps } = props;
    const classes = useInputClasses();
    const id = React.useState(Math.random().toString())[0];

    return (
        <span {...rootProps} className={clsx(classes.root, rootProps?.className)}>
            <MuiSwitch {...inputProps} id={id} className={clsx(classes.switch, inputProps.className)} color='default' />
            {label != undefined && <label htmlFor={id}>{label}</label>}
        </span>
    );
};
